import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function AboutPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '15+', label: 'Years Experience' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '24/7', label: 'Support Available' }
  ];

  const values = [
    {
      icon: 'ri-star-line',
      title: 'Excellence',
      description: 'We pursue perfection in every installation, ensuring the highest quality standards in all our work.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Integrity',
      description: 'Honest communication, transparent pricing, and ethical business practices guide everything we do.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We stay ahead of technology trends to bring you the most advanced smart home solutions available.'
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'Your success is our success. We build lasting relationships based on trust and exceptional service.'
    }
  ];

  const certifications = [
    {
      name: 'Control4',
      logo: 'https://public.readdy.ai/ai/img_res/185f2dbed72c4bf3b267cd0ba39ed319.jpg',
      description: 'Certified Control4 Dealer & Installer'
    },
    {
      name: 'Savant',
      logo: 'https://readdy.ai/api/search-image?query=Savant%20Systems%20official%20brand%20logo%20on%20clean%20white%20background%20luxury%20smart%20home%20automation%20company%20branding&width=200&height=100&seq=cert-savant&orientation=landscape',
      description: 'Authorized Savant Integrator'
    },
    {
      name: 'RTI',
      logo: 'https://readdy.ai/api/search-image?query=RTI%20Remote%20Technologies%20official%20brand%20logo%20on%20clean%20white%20background%20professional%20control%20systems%20company%20branding&width=200&height=100&seq=cert-rti&orientation=landscape',
      description: 'RTI Certified Professional'
    },
    {
      name: 'Brilliant',
      logo: 'https://readdy.ai/api/search-image?query=Brilliant%20Home%20official%20brand%20logo%20on%20clean%20white%20background%20modern%20smart%20home%20control%20company%20branding&width=200&height=100&seq=cert-brilliant&orientation=landscape',
      description: 'Brilliant Authorized Partner'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=professional%20technology%20team%20working%20together%20in%20modern%20office%20environment%20with%20smart%20home%20automation%20equipment%20and%20advanced%20networking%20tools%20collaborative%20workspace%20with%20blue%20accent%20lighting%20and%20contemporary%20design&width=1920&height=800&seq=about-hero-bg&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-[1400px] mx-auto px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About <span className="text-[#4A90E2]">RDM Security</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Leading the way in smart home automation and security solutions across South East Florida and West Coast Tampa South.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#4A90E2] mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our <span className="text-[#4A90E2]">Story</span>
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Founded with a vision to transform how people interact with their homes, <strong>RDM Security</strong> has grown to become one of Florida's most trusted names in <strong>home automation and security solutions</strong>.
                </p>
                <p>
                  With over 15 years of experience, we've completed more than 500 projects across <strong>South East Florida</strong> and <strong>West Coast Tampa South</strong>, earning a reputation for excellence, reliability, and innovation.
                </p>
                <p>
                  Our team of certified technicians specializes in premium platforms including <strong>Control4, Savant, RTI, and Brilliant</strong>, ensuring you receive the highest quality installation and support.
                </p>
                <p>
                  We don't just install technology—we create seamless experiences that enhance your lifestyle, increase your property value, and provide peace of mind through advanced security and automation.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20luxury%20smart%20home%20interior%20with%20integrated%20automation%20technology%20featuring%20elegant%20control%20panels%20ambient%20blue%20lighting%20and%20sophisticated%20contemporary%20design%20showcasing%20premium%20home%20automation%20installation%20in%20upscale%20residence&width=800&height=600&seq=about-story-img&orientation=landscape"
                alt="RDM Security Smart Home"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-[#4A90E2]/10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#4A90E2]">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our work and define our commitment to excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="text-center p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 hover:border-[#4A90E2] hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#3A7BC8] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Certified <span className="text-[#4A90E2]">Partners</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Authorized and certified by the industry's leading smart home technology brands.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-[#4A90E2] hover:shadow-xl transition-all text-center"
              >
                <img
                  src={cert.logo}
                  alt={cert.name}
                  className="w-full h-20 object-contain mb-4"
                />
                <p className="text-sm text-gray-600 font-medium">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="max-w-[1400px] mx-auto px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Experience the RDM Security difference. Contact us today to discuss your smart home vision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#schedule"
              className="px-8 py-4 bg-[#4A90E2] text-white text-base font-semibold rounded-full hover:bg-[#3A7BC8] hover:shadow-lg hover:shadow-[#4A90E2]/30 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Schedule Survey
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="tel:+17862829701"
              className="px-8 py-4 bg-white/10 text-white text-base font-semibold rounded-full hover:bg-white/20 border border-white/20 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              Call (786) 282-9701
              <i className="ri-phone-line"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
